import { useEffect, useState } from "react";
import i18n from "@/i18n";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/ne";
import "dayjs/locale/hi";
import enUS from "antd/locale/en_US";
import hiIN from "antd/locale/hi_IN";
import neNPBase from "antd/locale/ne_NP";

// AntD ships ne_NP, but its Pagination block is still the English one — the
// size selector read "20 / page" under a Nepali page. Everything else in the
// locale (calendar, date pickers) is AntD's own and is left as shipped.
//
// This used to be a top-level dynamic import() assigned into a `let`, which
// is read ONCE when ANTD_LOCALES is built below — before the import resolves —
// so Nepali always got en_US and the ne_NP file was never used at all.
const neNP: typeof enUS = {
  ...neNPBase,
  Pagination: {
    ...neNPBase.Pagination,
    items_per_page: "/ पृष्ठ",
    jump_to: "जानुहोस्",
    jump_to_confirm: "पुष्टि गर्नुहोस्",
    page: "पृष्ठ",
    prev_page: "अघिल्लो पृष्ठ",
    next_page: "अर्को पृष्ठ",
    prev_5: "अघिल्ला ५ पृष्ठ",
    next_5: "अर्का ५ पृष्ठ",
    prev_3: "अघिल्ला ३ पृष्ठ",
    next_3: "अर्का ३ पृष्ठ",
    page_size: "पृष्ठ आकार",
  },
};

const ANTD_LOCALES = { en: enUS, ne: neNP, hi: hiIN } as const;
const DAYJS_LOCALES = { en: "en", ne: "ne", hi: "hi" } as const;

type SupportedLanguage = keyof typeof ANTD_LOCALES;

function normalize(lang: string): SupportedLanguage {
  const head = lang.split("-")[0].toLowerCase();
  if (head === "ne" || head === "hi") return head;
  return "en";
}

/**
 * Single-source-of-truth hook for third-party locale sync. Mount once at
 * the layout root (ThemeProvider). Whenever i18n.language changes:
 *   - dayjs.locale(…) → date displays flip language
 *   - returned `antdLocale` flips → consumer plugs it into ConfigProvider
 */
export function useSyncThirdPartyLocale() {
  const initial = normalize(i18n.language);
  const [antdLocale, setAntdLocale] = useState(ANTD_LOCALES[initial]);

  useEffect(() => {
    dayjs.locale(DAYJS_LOCALES[normalize(i18n.language)]);
    setAntdLocale(ANTD_LOCALES[normalize(i18n.language)]);

    const onChange = (lng: string) => {
      const head = normalize(lng);
      dayjs.locale(DAYJS_LOCALES[head]);
      setAntdLocale(ANTD_LOCALES[head]);
    };
    i18n.on("languageChanged", onChange);
    return () => {
      i18n.off("languageChanged", onChange);
    };
  }, []);

  return { antdLocale };
}
